import type { Nudge, NudgeKind } from '@cobliteam/hooks/src/useNudge/types';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { NUDGE_SURFACE } from './nudgeSurface';

export interface NudgeCollapsedRowProps {
  nudge: Nudge;
  onExpand: () => void;
}

const ICON: Record<NudgeKind, { name: 'lightbulb' | 'sparkles'; className: string }> = {
  suggestion: { name: 'lightbulb', className: 'text-primary-9' },
  recommendation: { name: 'sparkles', className: 'text-accessory-purple-9' },
};

/**
 * A nudge waiting its turn in the floating stack: one line, click to expand.
 *
 * Renders `nudge.title` rather than `nudge.render()` — the body stays unmounted while collapsed,
 * so a collapsed nudge costs nothing beyond its own source hook (which runs regardless, since
 * eligibility is what put it in the stack).
 */
const NudgeCollapsedRow = ({ nudge, onExpand }: NudgeCollapsedRowProps) => {
  const icon = ICON[nudge.kind];

  return (
    <button
      type='button'
      data-testid={`nudge-collapsed-${nudge.id}`}
      onClick={onExpand}
      aria-expanded={false}
      className={`flex w-full items-center gap-2 ${NUDGE_SURFACE.shell} px-[14px] py-2.5 text-left ${NUDGE_SURFACE.shadowCollapsed} transition-colors hover:bg-neutral-50`}
    >
      <Icon name={icon.name} size='xs' className={`shrink-0 ${icon.className}`} />
      <Typography variant='description-list-default' className={`flex-1 truncate ${NUDGE_SURFACE.title}`}>
        {nudge.title}
      </Typography>
      <Icon name='chevron-down' size='sm' className={`shrink-0 ${NUDGE_SURFACE.muted}`} />
    </button>
  );
};

export default NudgeCollapsedRow;
